import { z } from 'zod/v4'

import {
  AuditReportType,
  DateRangeTemplate,
  ReportFormat,
  ReportRequestStatus,
  ScheduleType,
} from '../../contracts/admin/audit-reports.types.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'

export const AUDIT_REPORTS_DEFAULT_LIMIT = 20
export const AUDIT_REPORTS_MAX_LIMIT = 50
/** The most schedules the screen carries: `total` says how many exist, the client never deduces it from the length. */
export const AUDIT_REPORT_SCHEDULES_CAP = 50

/**
 * The query of the tab. `view` (history or schedules) is NOT here: it lives only in the URL of the client, because the
 * response always carries both lists and changing view is not a new request.
 */
export const auditReportsQuerySchema = z.strictObject({
  status: z.enum(['failed']).optional(),
  page: z.coerce.number<number>().int().min(1).max(10_000).default(1),
  limit: z.coerce
    .number<number>()
    .int()
    .min(1)
    .max(AUDIT_REPORTS_MAX_LIMIT)
    .default(AUDIT_REPORTS_DEFAULT_LIMIT),
})

const COMPLIANCE_TYPES = ['GDPR', 'SOC2', 'ALL'] as const
const OUTCOMES = ['success', 'failure', 'partial'] as const
const GROUPINGS = ['day', 'week', 'month', 'user', 'action', 'tenant'] as const

const textOrNull = z.string().nullable()
const numberOrNull = z.number().nullable()

/** `ReportFilters` of `audit-reports.types.ts`, key by key. */
export const reportFiltersSchema = z.strictObject({
  tenantId: z.string().optional(),
  userId: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  search: z.string().optional(),
  action: z.string().optional(),
  resourceType: z.string().optional(),
  complianceType: z.enum(COMPLIANCE_TYPES).optional(),
  eventType: z.string().optional(),
  outcome: z.enum(OUTCOMES).optional(),
  exportFormat: z.string().optional(),
  userRole: z.string().optional(),
  groupBy: z.enum(GROUPINGS).optional(),
})

/** `ScheduleConfig` of `audit-reports.types.ts`, key by key. */
export const scheduleConfigSchema = z.strictObject({
  hour: z.number().optional(),
  dayOfWeek: z.number().optional(),
  dayOfMonth: z.number().optional(),
  startMonth: z.number().optional(),
  month: z.number().optional(),
})

const reportOwnerSchema = z.strictObject({
  id: z.string(),
  email: z.string(),
  name: z.string(),
})

/** A row of the history: `ReportRequestResponse`, checked against it in both directions by a type assertion. */
export const reportRequestRowSchema = z.strictObject({
  id: z.string(),
  reportType: z.enum(AuditReportType),
  format: z.enum(ReportFormat),
  status: z.enum(ReportRequestStatus),
  progress: z.number(),
  filters: reportFiltersSchema,
  dateFrom: z.string(),
  dateTo: z.string(),
  fileName: textOrNull,
  fileSizeBytes: numberOrNull,
  recordCount: numberOrNull,
  errorMessage: textOrNull,
  emailRecipient: textOrNull,
  emailSentAt: textOrNull,
  downloadUrl: textOrNull,
  expiresAt: z.string(),
  scheduleId: textOrNull,
  createdAt: z.string(),
  startedAt: textOrNull,
  completedAt: textOrNull,
  processingTimeMs: numberOrNull,
  requestedBy: reportOwnerSchema,
  estimatedTime: z.string().optional(),
})

/** A schedule: `ReportScheduleResponse`, checked against it in both directions by a type assertion. */
export const reportScheduleRowSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  description: textOrNull,
  reportType: z.enum(AuditReportType),
  format: z.enum(ReportFormat),
  filters: reportFiltersSchema,
  dateRangeTemplate: z.enum(DateRangeTemplate),
  dateRangeOffsetDays: z.number(),
  dateRangeDays: numberOrNull,
  scheduleType: z.enum(ScheduleType),
  scheduleConfig: scheduleConfigSchema,
  emailRecipients: z.array(z.string()),
  emailSubject: textOrNull,
  isActive: z.boolean(),
  nextRunAt: textOrNull,
  lastRunAt: textOrNull,
  lastRunStatus: textOrNull,
  totalRuns: z.number(),
  successfulRuns: z.number(),
  failedRuns: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: reportOwnerSchema,
})

export const adminAuditReportsScreen = defineScreen({
  id: 'admin.audit.reports',
  path: '/api/screens/admin/audit/reports',
  access: 'super_admin',
  query: auditReportsQuerySchema,
  response: z.strictObject({
    /** Of the user who asks, like the two lists: the strip and the table come from the same partition. */
    counts: z.strictObject({
      totalReports: z.number().int().min(0),
      failedReports: z.number().int().min(0),
      activeSchedules: z.number().int().min(0),
    }),
    history: z.strictObject({
      items: z.array(reportRequestRowSchema),
      total: z.number().int().min(0),
      page: z.number().int().min(1),
      limit: z.number().int().min(1),
    }),
    schedules: z.strictObject({
      items: z.array(reportScheduleRowSchema).max(AUDIT_REPORT_SCHEDULES_CAP),
      total: z.number().int().min(0),
    }),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.audit'],
  budget: { requests: 1, statements: 5, bytes: 120_000, p95Ms: 200 },
  summary: 'Audit › Reports: own counts, one page of history and the schedules',
})
export type AuditReportsQuery = ScreenQuery<typeof adminAuditReportsScreen>
export type AuditReportsData = ScreenData<typeof adminAuditReportsScreen>
